import { useCallback } from "react"; // Import useCallback
import { useRecipeContext } from "../contexts/RecipeContext";
import { recipesApi } from "../api/recipes";
import { RecipeApiResponse } from "../types/recipe";

/**
 * Custom hook for managing recipe operations.
 * Provides access to recipe-related state and a function to fetch recipe data.
 * Uses the RecipeContext to share state across components.
 *
 * @returns {object} Contains recipe data, loading/error state, and fetchRecipe function.
 */
export const useRecipes = () => {
  // Destructure recipe context values and setters
  const {
    recipe,
    author,
    fullContributors,
    linkedRecipes,
    loading,
    error,
    setRecipe,
    setAuthor,
    setFullContributors,
    setLinkedRecipes,
    setLoading,
    setError,
  } = useRecipeContext();

  /**
   * Fetches recipe data from the API and updates context state.
   * Handles loading and error states.
   *
   * @param {string} recipeId - The ID of the recipe to fetch.
   */
  /**
   * NOTE:
   * useCallback is used here to memoize the fetchRecipe function.
   * Memoization ensures that fetchRecipe maintains the same reference between renders
   * unless its dependencies change. This is important because:
   * - It prevents unnecessary re-creations of the function, which can cause unwanted effects
   *   in components that depend on stable function references (e.g., useEffect, child props).
   * - It avoids triggering re-renders or effects in components that consume this hook,
   *   especially when fetchRecipe is passed as a prop or used in dependency arrays.
   * - In context-based hooks, memoizing functions helps maintain performance and prevents
   *   infinite loops or excessive API calls.
   * Without useCallback, fetchRecipe would be re-created on every render, potentially causing
   * bugs or performance issues.
   */
  const fetchRecipe = useCallback(
    async (recipeId: string) => {
      if (!recipeId) {
        setError("No recipe ID provided.");
        return;
      }
      setLoading(true);
      setError(null);

      try {
        // Call API to get recipe data
        const apiResponse: RecipeApiResponse = await recipesApi.getRecipe(
          recipeId
        );

        // Check if apiResponse is valid before setting state
        if (apiResponse) {
          // Update context state with fetched data
          setRecipe(apiResponse.recipe);
          setAuthor(apiResponse.author);
          setFullContributors(apiResponse.fullContributors);
          setLinkedRecipes(apiResponse.linkedRecipes);
        } else {
          setError("Recipe data not found.");
        }
      } catch (err) {
        setError("Could not load recipe data.");
        console.error("Error fetching recipe data:", err);
      } finally {
        setLoading(false);
      }
    },
    [
      setRecipe,
      setAuthor,
      setFullContributors,
      setLinkedRecipes,
      setLoading,
      setError,
    ]
  ); // Add all setters as dependencies

  // Return context state and fetch function for use in components
  return {
    recipe,
    author,
    fullContributors,
    linkedRecipes,
    loading,
    error,
    fetchRecipe,
  };
};
